/**
 * LinkedIn Ads Adapter (Story 10.3)
 *
 * Plain object implementing IntegrationAdapter — no class, no inheritance.
 * Uses safeFetch() for all external API calls.
 *
 * CRITICAL differences from LinkedIn Pages adapter:
 * - Uses urn:li:sponsoredAccount:{id} (not organization URNs)
 * - adAnalytics endpoint (not organizationalEntityShareStatistics)
 * - Custom date range format: (start:(year:Y,month:M,day:D),end:(...))
 * - costInLocalCurrency returned as BigDecimal string (parse with parseFloat)
 * - Max 20 fields per adAnalytics request
 * - Requires X-Restli-Protocol-Version: 2.0.0 header
 *
 * ADR-013: Adapter Pattern for API Isolation
 */

import type {
  DimensionDefinition,
  IntegrationAdapter,
  AdapterContext,
  OAuthCredentials,
  AdapterResult,
  AdapterMetrics,
  AccountInfo,
  MetricDefinition,
  RefreshedTokens,
} from "../adapter";
import { adapterOk, adapterErr } from "../adapter";
import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { LINKEDIN_API_VERSION, LINKEDIN_INTEGRATION_SCOPES } from "./scopes";
import { discoverLinkedInAdAccounts, type DiscoveredAdAccount } from "./discovery";
import { refreshLinkedInAccessToken } from "./token-refresh";
import { parseLinkedInError } from "./errors";

// ============================================================================
// Constants
// ============================================================================

/** LinkedIn REST API base — same as Pages adapter */
const LINKEDIN_REST_BASE = "https://api.linkedin.com/rest";

/** Maximum data retention for LinkedIn Ads analytics (2 years) */
const MAX_RETENTION_DAYS = 730;

/** Maximum campaign IDs per batch name resolution request */
const CAMPAIGN_BATCH_SIZE = 20;

/** Safety limit for total campaigns to fetch */
const MAX_CAMPAIGNS = 200;

/**
 * LinkedIn adAnalytics allows max 20 fields per request.
 * With 50+ metrics, we split into 3 core batches + 1 optional revenue attribution call.
 *
 * Batch 1 (20): Core delivery + engagement + conversions
 * Batch 2 (18): Video + document + messaging + lead gen
 * Batch 3 (19): Social actions + carousel + top 10 viral
 * Batch 4 (separate endpoint): Revenue attribution (q=attributedRevenueMetrics)
 */
const LINKEDIN_ADS_BATCH_1 = [
  "impressions",
  "clicks",
  "costInLocalCurrency",
  "costInUsd",
  "approximateMemberReach",
  "externalWebsiteConversions",
  "externalWebsitePostClickConversions",
  "externalWebsitePostViewConversions",
  "conversionValueInLocalCurrency",
  "oneClickLeads",
  "oneClickLeadFormOpens",
  "qualifiedLeads",
  "costPerQualifiedLead",
  "validWorkEmailLeads",
  "totalEngagements",
  "likes",
  "reactions",
  "comments",
  "shares",
  "follows",
] as const;

const LINKEDIN_ADS_BATCH_2 = [
  "videoStarts",
  "videoViews",
  "videoFirstQuartileCompletions",
  "videoMidpointCompletions",
  "videoThirdQuartileCompletions",
  "videoCompletions",
  "fullScreenPlays",
  "videoWatchTime",
  "averageVideoWatchTime",
  "documentFirstQuartileCompletions",
  "documentMidpointCompletions",
  "documentThirdQuartileCompletions",
  "documentCompletions",
  "sends",
  "opens",
  "actionClicks",
  "textUrlClicks",
  "adUnitClicks",
] as const;

const LINKEDIN_ADS_BATCH_3 = [
  "commentLikes",
  "otherEngagements",
  "landingPageClicks",
  "companyPageClicks",
  "subscriptionClicks",
  "averageDwellTime",
  "audiencePenetration",
  "cardClicks",
  "cardImpressions",
  // Top 10 viral metrics — every standard metric has a viral* counterpart for organic re-shares.
  // 19 total fields in this batch, within the 20-field API limit.
  "viralImpressions",
  "viralClicks",
  "viralLikes",
  "viralComments",
  "viralShares",
  "viralVideoViews",
  "viralVideoCompletions",
  "viralReactions",
  "viralTotalEngagements",
  "viralFollows",
] as const;

/** Legacy alias for backward compatibility with tests */
const LINKEDIN_ADS_FIELDS = LINKEDIN_ADS_BATCH_1;

/** Days of week lookup */
const DAYS_OF_WEEK = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// ============================================================================
// Types (local to adapter, NOT exported)
// ============================================================================

interface AdAnalyticsElement {
  // Batch 1: Core delivery + engagement + conversions
  impressions?: number;
  clicks?: number;
  costInLocalCurrency?: string;
  costInUsd?: string;
  approximateMemberReach?: number;
  externalWebsiteConversions?: number;
  externalWebsitePostClickConversions?: number;
  externalWebsitePostViewConversions?: number;
  conversionValueInLocalCurrency?: string;
  oneClickLeads?: number;
  oneClickLeadFormOpens?: number;
  qualifiedLeads?: number;
  costPerQualifiedLead?: string;
  validWorkEmailLeads?: number;
  totalEngagements?: number;
  likes?: number;
  reactions?: number;
  comments?: number;
  shares?: number;
  follows?: number;
  // Batch 2: Video + document + messaging + lead gen
  videoStarts?: number;
  videoViews?: number;
  videoFirstQuartileCompletions?: number;
  videoMidpointCompletions?: number;
  videoThirdQuartileCompletions?: number;
  videoCompletions?: number;
  fullScreenPlays?: number;
  videoWatchTime?: number;
  averageVideoWatchTime?: number;
  documentFirstQuartileCompletions?: number;
  documentMidpointCompletions?: number;
  documentThirdQuartileCompletions?: number;
  documentCompletions?: number;
  sends?: number;
  opens?: number;
  actionClicks?: number;
  textUrlClicks?: number;
  adUnitClicks?: number;
  // Batch 3: Social extras + carousel + engagement depth
  commentLikes?: number;
  otherEngagements?: number;
  landingPageClicks?: number;
  companyPageClicks?: number;
  subscriptionClicks?: number;
  averageDwellTime?: number;
  audiencePenetration?: number;
  cardClicks?: number;
  cardImpressions?: number;
  viralImpressions?: number;
  viralClicks?: number;
  viralLikes?: number;
  viralComments?: number;
  viralShares?: number;
  viralVideoViews?: number;
  viralVideoCompletions?: number;
  viralReactions?: number;
  viralTotalEngagements?: number;
  viralFollows?: number;
  // Date range
  dateRange: {
    start: { year: number; month: number; day: number };
    end: { year: number; month: number; day: number };
  };
  pivotValues?: string[];
}

interface AdAnalyticsResponse {
  elements: AdAnalyticsElement[];
  paging?: { count: number; start: number; links: unknown[] };
}

/** Revenue attribution response (q=attributedRevenueMetrics) */
interface RevenueAttributionResponse {
  elements: Array<{
    revenueWonInUsd?: string;
    returnOnAdSpend?: number;
    closedWonOpportunities?: number;
    averageDealSizeInUsd?: string;
    averageDaysToClose?: number;
    dateRange: {
      start: { year: number; month: number; day: number };
      end: { year: number; month: number; day: number };
    };
    pivotValues?: string[];
  }>;
}

interface AdCampaignResponse {
  id: number;
  name: string;
  status: string;
  account: string;
  costType: string;
  type: string;
}


interface AdCampaignsBatchResponse {
  results: Record<string, AdCampaignResponse>;
  errors: Record<string, unknown>;
  statuses: Record<string, number>;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Builds LinkedIn Ads REST API headers.
 * Ads requires X-Restli-Protocol-Version in addition to standard headers.
 */
function linkedInAdsHeaders(accessToken: string): Record<string, string> {
  return {
    Authorization: `Bearer ${accessToken}`,
    "LinkedIn-Version": LINKEDIN_API_VERSION,
    "X-Restli-Protocol-Version": "2.0.0",
  };
}

/**
 * Formats a date range for the LinkedIn adAnalytics API.
 * CRITICAL: Uses UTC methods to avoid timezone issues in Cloudflare Workers.
 * End date is EXCLUSIVE — add 1 day to include the requested end date.
 */
function formatLinkedInDateRange(start: Date, end: Date): string {
  // Add 1 day to end date (exclusive end)
  const inclusiveEnd = new Date(end);
  inclusiveEnd.setUTCDate(inclusiveEnd.getUTCDate() + 1);

  return `(start:(year:${start.getUTCFullYear()},month:${start.getUTCMonth() + 1},day:${start.getUTCDate()}),end:(year:${inclusiveEnd.getUTCFullYear()},month:${inclusiveEnd.getUTCMonth() + 1},day:${inclusiveEnd.getUTCDate()}))`;
}

/**
 * Clamps start date to max 730 days ago (LinkedIn Ads data retention).
 */
function clampAdsDateRange(start: Date, end: Date): { effectiveStart: Date; effectiveEnd: Date; clamped: boolean } {
  const now = new Date();
  const maxStart = new Date(now);
  maxStart.setUTCDate(maxStart.getUTCDate() - MAX_RETENTION_DAYS);
  // Normalize to midnight UTC to avoid time-of-day bias in boundary comparisons
  maxStart.setUTCHours(0, 0, 0, 0);

  let clamped = false;
  let effectiveStart = start;
  if (start < maxStart) {
    effectiveStart = maxStart;
    clamped = true;
  }

  return { effectiveStart, effectiveEnd: end, clamped };
}

function getDayOfWeek(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00Z");
  return DAYS_OF_WEEK[d.getUTCDay()] ?? "Unknown";
}

/**
 * Safely parses a BigDecimal string to a number.
 * Handles empty strings, null, undefined — never returns NaN.
 */
function parseSpend(value: string | null | undefined): number {
  if (!value || value === "") return 0;
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** Round currency values to 2 decimal places */
function roundCurrency(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Round rate values to 4 decimal places */
function roundRate(value: number): number {
  return Math.round(value * 10000) / 10000;
}

/**
 * Builds a flat metric record from a merged AdAnalyticsElement.
 * Includes all raw + derived metrics (62 fields) with division-by-zero guards.
 */
function buildMetricRecord(el: AdAnalyticsElement, dateStr: string): Record<string, unknown> {
  const impressions = el.impressions ?? 0;
  const clicks = el.clicks ?? 0;
  const spend = parseSpend(el.costInLocalCurrency);
  const spendUsd = parseSpend(el.costInUsd);
  const conversions = el.externalWebsiteConversions ?? 0;
  const leads = el.oneClickLeads ?? 0;

  // Derived metrics with division-by-zero guards
  const ctr = impressions > 0 ? roundRate(clicks / impressions) : 0;
  const cpc = clicks > 0 ? roundCurrency(spend / clicks) : 0;
  const cpm = impressions > 0 ? roundCurrency((spend / impressions) * 1000) : 0;
  const costPerConversion = conversions > 0 ? roundCurrency(spend / conversions) : 0;
  const costPerLead = leads > 0 ? roundCurrency(spend / leads) : 0;

  return {
    date: dateStr,
    day_of_week: getDayOfWeek(dateStr),
    // Core delivery
    impressions,
    clicks,
    spend: roundCurrency(spend),
    spend_usd: roundCurrency(spendUsd),
    reach: el.approximateMemberReach ?? 0,
    // Derived cost
    ctr,
    cpc,
    cpm,
    audience_penetration: el.audiencePenetration ?? 0,
    // Engagement & Social
    total_engagements: el.totalEngagements ?? 0,
    likes: el.likes ?? 0,
    reactions: el.reactions ?? 0,
    comments: el.comments ?? 0,
    comment_likes: el.commentLikes ?? 0,
    shares: el.shares ?? 0,
    follows: el.follows ?? 0,
    other_engagements: el.otherEngagements ?? 0,
    // Click types
    landing_page_clicks: el.landingPageClicks ?? 0,
    company_page_clicks: el.companyPageClicks ?? 0,
    subscription_clicks: el.subscriptionClicks ?? 0,
    card_clicks: el.cardClicks ?? 0,
    card_impressions: el.cardImpressions ?? 0,
    // Engagement depth
    average_dwell_time: el.averageDwellTime ?? 0,
    // Conversions
    conversions,
    post_click_conversions: el.externalWebsitePostClickConversions ?? 0,
    view_through_conversions: el.externalWebsitePostViewConversions ?? 0,
    conversion_value: roundCurrency(parseSpend(el.conversionValueInLocalCurrency)),
    cost_per_conversion: costPerConversion,
    // Lead gen
    leads,
    lead_form_opens: el.oneClickLeadFormOpens ?? 0,
    qualified_leads: el.qualifiedLeads ?? 0,
    // LinkedIn API returns costPerQualifiedLead as a BigDecimal string (same as costInLocalCurrency)
    cost_per_qualified_lead: roundCurrency(parseSpend(el.costPerQualifiedLead)),
    valid_work_email_leads: el.validWorkEmailLeads ?? 0,
    cost_per_lead: costPerLead,
    // Video
    video_starts: el.videoStarts ?? 0,
    video_views: el.videoViews ?? 0,
    video_first_quartile: el.videoFirstQuartileCompletions ?? 0,
    video_midpoint: el.videoMidpointCompletions ?? 0,
    video_third_quartile: el.videoThirdQuartileCompletions ?? 0,
    video_completions: el.videoCompletions ?? 0,
    full_screen_plays: el.fullScreenPlays ?? 0,
    video_watch_time: el.videoWatchTime ?? 0,
    avg_video_watch_time: el.averageVideoWatchTime ?? 0,
    // Document ad
    document_first_quartile: el.documentFirstQuartileCompletions ?? 0,
    document_midpoint: el.documentMidpointCompletions ?? 0,
    document_third_quartile: el.documentThirdQuartileCompletions ?? 0,
    document_completions: el.documentCompletions ?? 0,
    // Sponsored messaging
    messaging_sends: el.sends ?? 0,
    messaging_opens: el.opens ?? 0,
    messaging_action_clicks: el.actionClicks ?? 0,
    messaging_text_url_clicks: el.textUrlClicks ?? 0,
    messaging_ad_unit_clicks: el.adUnitClicks ?? 0,
    // Viral (top 10 — organic re-shares of paid content)
    viral_impressions: el.viralImpressions ?? 0,
    viral_clicks: el.viralClicks ?? 0,
    viral_likes: el.viralLikes ?? 0,
    viral_comments: el.viralComments ?? 0,
    viral_shares: el.viralShares ?? 0,
    viral_video_views: el.viralVideoViews ?? 0,
    viral_video_completions: el.viralVideoCompletions ?? 0,
    viral_reactions: el.viralReactions ?? 0,
    viral_total_engagements: el.viralTotalEngagements ?? 0,
    viral_follows: el.viralFollows ?? 0,
  };
}

/**
 * Extracts campaign ID from a URN like urn:li:sponsoredCampaign:12345
 */
function parseCampaignUrn(urn: string): string | null {
  const match = urn.match(/^urn:li:sponsoredCampaign:(\d+)$/);
  return match?.[1] ?? null;
}

/**
 * Converts LinkedIn date range start object to YYYY-MM-DD string.
 */
function linkedInDateToStr(d: { year: number; month: number; day: number }): string {
  const month = String(d.month).padStart(2, "0");
  const day = String(d.day).padStart(2, "0");
  return `${d.year}-${month}-${day}`;
}

/**
 * Handles LinkedIn API error responses.
 * Attempts to parse LinkedIn-specific error format, falls back to safeFetch mapping.
 */
async function handleLinkedInAdsFetchError(
  fetchResult: { success: false; error: { code: string; message: string; retryable: boolean; retryAfterMs?: number; providerStatus?: number; providerMessage?: string } }
): Promise<AdapterResult<never>> {
  if (fetchResult.error.providerMessage) {
    const linkedInError = parseLinkedInError(
      fetchResult.error.providerMessage,
      undefined
    );
    if (linkedInError) {
      return adapterErr({
        code: linkedInError.code,
        message: sanitizeErrorMessage(linkedInError.message),
        retryable: linkedInError.retryable,
        retryAfterMs: linkedInError.retryAfterMs ?? fetchResult.error.retryAfterMs,
        providerStatus: fetchResult.error.providerStatus,
      });
    }
  }

  return adapterErr(fetchResult.error as Parameters<typeof adapterErr>[0]);
}

/**
 * Validates that required LinkedIn Ads scopes are present.
 */
function validateAdsScopes(scopes: string[]): boolean {
  const scopeSet = new Set(scopes);
  return scopeSet.has("r_ads") && scopeSet.has("r_ads_reporting");
}

// ============================================================================
// Metric Definitions (Subtask 1.2)
// ============================================================================

export const LINKEDIN_ADS_METRICS: MetricDefinition[] = [
  // Core Delivery & Cost
  { key: "impressions", label: "Impressions", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "clicks", label: "Clicks", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "spend", label: "Spend", type: "currency", category: "Spend", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "spend_usd", label: "Spend (USD)", type: "currency", category: "Spend", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "reach", label: "Reach", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  // Derived Cost
  { key: "ctr", label: "CTR", type: "percentage", category: "Performance", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "clicks", denominator: "impressions" }, compatibleWidgets: ["kpi", "line-chart", "gauge"] },
  { key: "cpc", label: "CPC", type: "currency", category: "Efficiency", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "spend", denominator: "clicks" }, compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "cpm", label: "CPM", type: "currency", category: "Efficiency", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "spend", denominator: "impressions" }, compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "audience_penetration", label: "Audience Penetration", type: "percentage", category: "Delivery", dataType: "Percentage", aggregationKind: "ratio", compatibleWidgets: ["kpi", "gauge"] },
  // Engagement & Social
  { key: "total_engagements", label: "Total Engagements", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "likes", label: "Likes", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "reactions", label: "Reactions", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "comments", label: "Comments", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "comment_likes", label: "Comment Likes", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "shares", label: "Shares", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "follows", label: "Follows", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "other_engagements", label: "Other Engagements", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  // Click Types
  { key: "landing_page_clicks", label: "Landing Page Clicks", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "company_page_clicks", label: "Company Page Clicks", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "subscription_clicks", label: "Subscription Clicks", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "card_clicks", label: "Carousel Card Clicks", type: "number", category: "Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "card_impressions", label: "Carousel Card Impressions", type: "number", category: "Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  // Engagement Depth
  { key: "average_dwell_time", label: "Avg Dwell Time", type: "duration", category: "Engagement", dataType: "Duration", aggregationKind: "ratio", ratioComponents: { denominator: "impressions" }, compatibleWidgets: ["kpi"] },
  // Conversions
  { key: "conversions", label: "Conversions", type: "number", category: "Conversion", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "post_click_conversions", label: "Post-Click Conversions", type: "number", category: "Conversion", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "view_through_conversions", label: "View-Through Conversions", type: "number", category: "Conversion", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "conversion_value", label: "Conversion Value", type: "currency", category: "Conversion", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "cost_per_conversion", label: "Cost per Conversion", type: "currency", category: "Efficiency", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "spend", denominator: "conversions" }, compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"] },
  // Lead Gen
  { key: "leads", label: "Leads", type: "number", category: "Conversion", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "lead_form_opens", label: "Lead Form Opens", type: "number", category: "Conversion", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "qualified_leads", label: "Qualified Leads", type: "number", category: "Conversion", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "cost_per_qualified_lead", label: "Cost per Qualified Lead", type: "currency", category: "Efficiency", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "spend", denominator: "qualified_leads" }, compatibleWidgets: ["kpi"] },
  { key: "valid_work_email_leads", label: "Valid Work Email Leads", type: "number", category: "Conversion", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "cost_per_lead", label: "Cost per Lead", type: "currency", category: "Efficiency", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "spend", denominator: "leads" }, compatibleWidgets: ["kpi", "line-chart", "bar-chart", "data-table"] },
  // Video
  { key: "video_starts", label: "Video Starts", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "video_views", label: "Video Views", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "video_first_quartile", label: "Video 25%", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "funnel"] },
  { key: "video_midpoint", label: "Video 50%", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "funnel"] },
  { key: "video_third_quartile", label: "Video 75%", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "funnel"] },
  { key: "video_completions", label: "Video 100%", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "funnel"] },
  { key: "full_screen_plays", label: "Full Screen Plays", type: "number", category: "Video", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "video_watch_time", label: "Video Watch Time", type: "duration", category: "Video", dataType: "Duration", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "avg_video_watch_time", label: "Avg Video Watch Time", type: "duration", category: "Video", dataType: "Duration", aggregationKind: "ratio", ratioComponents: { numerator: "video_watch_time", denominator: "video_views" }, compatibleWidgets: ["kpi"] },
  // Document Ad
  { key: "document_first_quartile", label: "Document 25%", type: "number", category: "Document", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "document_midpoint", label: "Document 50%", type: "number", category: "Document", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "document_third_quartile", label: "Document 75%", type: "number", category: "Document", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "document_completions", label: "Document 100%", type: "number", category: "Document", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  // Sponsored Messaging
  { key: "messaging_sends", label: "Sends", type: "number", category: "Messaging", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "messaging_opens", label: "Opens", type: "number", category: "Messaging", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "messaging_action_clicks", label: "Action Clicks", type: "number", category: "Messaging", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "messaging_text_url_clicks", label: "Text URL Clicks", type: "number", category: "Messaging", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "messaging_ad_unit_clicks", label: "Ad Unit Clicks", type: "number", category: "Messaging", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  // Revenue Attribution (CRM)
  { key: "revenue_won_usd", label: "Revenue Won (USD)", type: "currency", category: "Revenue", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "return_on_ad_spend", label: "ROAS (CRM)", type: "number", category: "Revenue", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { numerator: "revenue_won_usd", denominator: "spend" }, compatibleWidgets: ["kpi", "gauge"] },
  { key: "closed_won_opportunities", label: "Closed-Won Opportunities", type: "number", category: "Revenue", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "avg_deal_size_usd", label: "Avg Deal Size", type: "currency", category: "Revenue", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "revenue_won_usd", denominator: "closed_won_opportunities" }, compatibleWidgets: ["kpi"] },
  { key: "avg_days_to_close", label: "Avg Days to Close", type: "number", category: "Revenue", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { denominator: "closed_won_opportunities" }, compatibleWidgets: ["kpi"] },
  // Viral (top 10 — organic re-shares of paid content)
  { key: "viral_impressions", label: "Viral Impressions", type: "number", category: "Viral", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "viral_clicks", label: "Viral Clicks", type: "number", category: "Viral", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "viral_likes", label: "Viral Likes", type: "number", category: "Viral", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "viral_comments", label: "Viral Comments", type: "number", category: "Viral", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "viral_shares", label: "Viral Shares", type: "number", category: "Viral", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "viral_video_views", label: "Viral Video Views", type: "number", category: "Viral", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "viral_video_completions", label: "Viral Video Completions", type: "number", category: "Viral", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "viral_reactions", label: "Viral Reactions", type: "number", category: "Viral", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "viral_total_engagements", label: "Viral Total Engagements", type: "number", category: "Viral", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "viral_follows", label: "Viral Follows", type: "number", category: "Viral", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
];

// ============================================================================
// Dimension Definitions (Subtask 1.3)
// ============================================================================

export const LINKEDIN_ADS_DIMENSIONS: DimensionDefinition[] = [
  // Time
  { key: "date", label: "Date", type: "Time", exampleValues: ["2026-03-01", "2026-03-02"] },
  { key: "day_of_week", label: "Day of Week", type: "Time", exampleValues: ["Monday", "Tuesday"] },
  // Campaign Structure
  { key: "campaign_id", label: "Campaign ID", type: "Structure", exampleValues: ["urn:li:sponsoredCampaign:12345"] },
  { key: "campaign_name", label: "Campaign Name", type: "Structure", exampleValues: ["B2B Lead Gen"] },
  { key: "campaign_status", label: "Campaign Status", type: "Structure", exampleValues: ["ACTIVE", "PAUSED"] },
  { key: "campaign_group", label: "Campaign Group", type: "Structure", exampleValues: ["urn:li:sponsoredCampaignGroup:100"] },
  { key: "creative", label: "Creative", type: "Structure", exampleValues: ["urn:li:sponsoredCreative:200"] },
  { key: "share", label: "Share", type: "Structure", exampleValues: ["urn:li:share:300"] },
  // Professional Demographics (Analytics Finder only)
  { key: "member_company_size", label: "Company Size", type: "Demo", exampleValues: ["SIZE_10001_OR_MORE", "SIZE_201_TO_500"] },
  { key: "member_industry", label: "Industry", type: "Demo", exampleValues: ["Computer Software", "Financial Services"] },
  { key: "member_seniority", label: "Seniority", type: "Demo", exampleValues: ["SENIOR", "MANAGER", "DIRECTOR"] },
  { key: "member_job_function", label: "Job Function", type: "Demo", exampleValues: ["Engineering", "Marketing"] },
  { key: "member_job_title", label: "Job Title", type: "Demo", exampleValues: ["Software Engineer", "VP Marketing"] },
  { key: "member_company", label: "Member Company", type: "Demo", exampleValues: ["urn:li:organization:1234"] },
  // Geography
  { key: "member_country", label: "Country", type: "Geo", exampleValues: ["urn:li:country:us", "urn:li:country:gb"] },
  { key: "member_region", label: "Region", type: "Geo", exampleValues: ["urn:li:region:4800", "urn:li:region:6003"] },
  { key: "member_county", label: "County", type: "Geo", exampleValues: ["urn:li:county:10101"] },
  // Other
  { key: "conversion", label: "Conversion Rule", type: "Conversion", exampleValues: ["urn:lla:llaPartnerConversion:12345"] },
  { key: "serving_location", label: "Serving Location", type: "Delivery", exampleValues: ["ON_SITE", "OFF_SITE"] },
  { key: "placement_name", label: "Placement Name", type: "Delivery", exampleValues: ["feed", "right_rail"] },
  { key: "device_type", label: "Device Type", type: "Tech", exampleValues: ["DESKTOP", "MOBILE", "TABLET"] },
  { key: "card_index", label: "Card Index", type: "Content", exampleValues: ["0", "1", "2"] },
  { key: "objective_type", label: "Objective Type", type: "Structure", exampleValues: ["LEAD_GENERATION", "BRAND_AWARENESS"] },
  { key: "event_stage", label: "Event Stage", type: "Conversion", exampleValues: ["EMAIL_CLICK", "FORM_SUBMISSION"] },
];

// ============================================================================
// LinkedIn Ads Adapter (Subtask 1.1)
// ============================================================================

export const linkedinAdsAdapter: IntegrationAdapter = {
  platform: "linkedin-ads",

  rateLimits: {
    requestsPerMinute: 10,
    requestsPerDay: 50,
  },

  // --------------------------------------------------------------------------
  // fetchMetrics (IMC-13: Expanded to 67 metrics via 3 batched requests + revenue attribution)
  // --------------------------------------------------------------------------
  async fetchMetrics(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
    dateRange: { start: Date; end: Date }
  ): Promise<AdapterResult<AdapterMetrics>> {
    const accountId = ctx.accountId;
    if (!accountId) {
      return adapterErr({
        code: "account_not_found",
        message: "No LinkedIn Ad Account selected. Please select an ad account first.",
        retryable: false,
      });
    }

    // Scope validation
    if (!validateAdsScopes(credentials.scopes)) {
      return adapterErr({
        code: "permission_denied",
        message: "LinkedIn Ads requires r_ads and r_ads_reporting scopes. Please reconnect LinkedIn with Ads permissions.",
        retryable: false,
      });
    }

    const headers = linkedInAdsHeaders(credentials.accessToken);

    // Clamp date range to 730 days
    const { effectiveStart, effectiveEnd, clamped } = clampAdsDateRange(dateRange.start, dateRange.end);

    if (clamped) {
      console.log(JSON.stringify({
        event: "linkedin_ads_date_clamped",
        accountId,
        tenantId: ctx.tenantId,
        originalStart: dateRange.start.toISOString(),
        clampedTo: effectiveStart.toISOString(),
      }));
    }

    const dateRangeParam = formatLinkedInDateRange(effectiveStart, effectiveEnd);
    const accountsParam = `List(${encodeURIComponent(`urn:li:sponsoredAccount:${accountId}`)})`;

    // ---- Fetch daily data via 3 batched adAnalytics requests ----
    const batchConfigs = [
      { name: "batch1", fields: LINKEDIN_ADS_BATCH_1 },
      { name: "batch2", fields: LINKEDIN_ADS_BATCH_2 },
      { name: "batch3", fields: LINKEDIN_ADS_BATCH_3 },
    ] as const;

    // Fetch all 3 batches for daily summary (pivot=ACCOUNT, timeGranularity=DAILY)
    const dailyBatchResults: AdAnalyticsResponse[] = [];
    for (const batch of batchConfigs) {
      const fieldsParam = batch.fields.join(",");
      const url = `${LINKEDIN_REST_BASE}/adAnalytics?q=analytics&pivot=ACCOUNT&accounts=${accountsParam}&dateRange=${dateRangeParam}&timeGranularity=DAILY&fields=${fieldsParam}`;

      const result = await safeFetch(url, { headers }, 25_000);

      if (!result.success) {
        // Batch 1 is critical, batches 2-3 degrade gracefully
        if (batch.name === "batch1") {
          return handleLinkedInAdsFetchError(result);
        }
        console.log(JSON.stringify({
          event: "linkedin_ads_batch_fetch_warning",
          accountId,
          tenantId: ctx.tenantId,
          batch: batch.name,
          errorCode: result.error.code,
        }));
        dailyBatchResults.push({ elements: [] });
        continue;
      }

      try {
        dailyBatchResults.push((await result.data.json()) as AdAnalyticsResponse);
      } catch (err) {
        if (batch.name === "batch1") {
          return adapterErr({
            code: "invalid_response",
            message: sanitizeErrorMessage(
              `Failed to parse LinkedIn ad analytics response: ${err instanceof Error ? err.message : String(err)}`
            ),
            retryable: false,
          });
        }
        dailyBatchResults.push({ elements: [] });
      }
    }

    // Merge batched daily results by date key
    const dailyByDate = new Map<string, AdAnalyticsElement>();
    for (const batchData of dailyBatchResults) {
      for (const el of batchData.elements ?? []) {
        const dateStr = linkedInDateToStr(el.dateRange.start);
        const existing = dailyByDate.get(dateStr);
        if (existing) {
          Object.assign(existing, el);
        } else {
          dailyByDate.set(dateStr, { ...el });
        }
      }
    }

    // Build dailySummary records
    const records: Record<string, unknown>[] = [];

    for (const [dateStr, el] of dailyByDate) {
      records.push({
        _reportType: "dailySummary",
        _date: dateStr,
        ...buildMetricRecord(el, dateStr),
      });
    }

    // Sort dailySummary by date
    records.sort((a, b) => String(a.date).localeCompare(String(b.date)));

    // ---- Fetch campaign breakdown (pivot=CAMPAIGN, timeGranularity=ALL) ----
    // Only fetch batch 1 for campaign breakdown (most important metrics)
    const campaignFieldsParam = LINKEDIN_ADS_BATCH_1.join(",");
    const campaignStatsUrl = `${LINKEDIN_REST_BASE}/adAnalytics?q=analytics&pivot=CAMPAIGN&accounts=${accountsParam}&dateRange=${dateRangeParam}&timeGranularity=ALL&fields=${campaignFieldsParam}`;

    const campaignStatsResult = await safeFetch(campaignStatsUrl, { headers }, 25_000);

    if (campaignStatsResult.success) {
      let campaignStatsData: AdAnalyticsResponse;
      try {
        campaignStatsData = (await campaignStatsResult.data.json()) as AdAnalyticsResponse;
      } catch (err) {
        console.log(JSON.stringify({
          event: "linkedin_ads_campaign_stats_parse_warning",
          accountId,
          tenantId: ctx.tenantId,
          error: err instanceof Error ? err.message : String(err),
        }));
        campaignStatsData = { elements: [] };
      }

      // Collect campaign IDs for name resolution
      const campaignElements = campaignStatsData.elements ?? [];
      const campaignIds: string[] = [];
      const campaignStatsByUrn = new Map<string, AdAnalyticsElement>();

      for (const el of campaignElements) {
        if (el.pivotValues?.[0]) {
          const urn = el.pivotValues[0];
          const id = parseCampaignUrn(urn);
          if (id) {
            campaignIds.push(id);
            campaignStatsByUrn.set(urn, el);
          }
        }
      }

      // Batch-resolve campaign names (max 20 per request)
      const campaignNames = new Map<string, { name: string; status: string }>();

      for (let i = 0; i < campaignIds.length && i < MAX_CAMPAIGNS; i += CAMPAIGN_BATCH_SIZE) {
        const batch = campaignIds.slice(i, i + CAMPAIGN_BATCH_SIZE);
        const idsParam = `List(${batch.join(",")})`;
        const batchUrl = `${LINKEDIN_REST_BASE}/adCampaigns?ids=${idsParam}`;

        const batchResult = await safeFetch(batchUrl, { headers }, 15_000);

        if (batchResult.success) {
          try {
            const batchData = (await batchResult.data.json()) as AdCampaignsBatchResponse;
            for (const [key, campaign] of Object.entries(batchData.results ?? {})) {
              campaignNames.set(key, {
                name: campaign.name ?? `Campaign ${key}`,
                status: campaign.status ?? "UNKNOWN",
              });
            }
          } catch {
            for (const id of batch) {
              if (!campaignNames.has(id)) {
                campaignNames.set(id, { name: `Campaign ${id}`, status: "UNKNOWN" });
              }
            }
          }
        } else {
          for (const id of batch) {
            campaignNames.set(id, { name: `Campaign ${id}`, status: "UNKNOWN" });
          }
        }
      }

      // Build campaign records
      const rangeStartStr = linkedInDateToStr({
        year: effectiveStart.getUTCFullYear(),
        month: effectiveStart.getUTCMonth() + 1,
        day: effectiveStart.getUTCDate(),
      });

      for (const [urn, el] of campaignStatsByUrn) {
        const id = parseCampaignUrn(urn);
        const info = id ? campaignNames.get(id) : undefined;

        // Campaign records are range-aggregated — day_of_week is not meaningful
        const campaignMetrics = buildMetricRecord(el, rangeStartStr);
        delete campaignMetrics.day_of_week;
        records.push({
          _reportType: "campaigns",
          _date: rangeStartStr,
          campaign_id: urn,
          campaign_name: info?.name ?? `Campaign ${id ?? "unknown"}`,
          campaign_status: info?.status ?? "UNKNOWN",
          ...campaignMetrics,
        });
      }
    } else {
      console.log(JSON.stringify({
        event: "linkedin_ads_campaign_stats_fetch_warning",
        accountId,
        tenantId: ctx.tenantId,
        errorCode: campaignStatsResult.error.code,
      }));
    }

    // ---- Revenue Attribution (optional, separate q=attributedRevenueMetrics) ----
    const daysDiff = Math.round((effectiveEnd.getTime() - effectiveStart.getTime()) / (24 * 60 * 60 * 1000));
    // Revenue attribution requires 30-365 day range, within last 1 year.
    // LinkedIn API docs say "30-366 days" but we cap at 365 to stay within the "within last 1 year" constraint.
    if (daysDiff >= 30 && daysDiff <= 365) {
      const revenueUrl = `${LINKEDIN_REST_BASE}/adAnalytics?q=attributedRevenueMetrics&accounts=${accountsParam}&dateRange=${dateRangeParam}&fields=revenueWonInUsd,returnOnAdSpend,closedWonOpportunities,averageDealSizeInUsd,averageDaysToClose`;

      const revenueResult = await safeFetch(revenueUrl, { headers }, 15_000);

      if (revenueResult.success) {
        try {
          const revenueData = (await revenueResult.data.json()) as RevenueAttributionResponse;
          const rangeStartStr = linkedInDateToStr({
            year: effectiveStart.getUTCFullYear(),
            month: effectiveStart.getUTCMonth() + 1,
            day: effectiveStart.getUTCDate(),
          });

          for (const el of revenueData.elements ?? []) {
            records.push({
              _reportType: "revenueAttribution",
              _date: rangeStartStr,
              date: rangeStartStr,
              revenue_won_usd: roundCurrency(parseSpend(el.revenueWonInUsd)),
              return_on_ad_spend: el.returnOnAdSpend ?? 0,
              closed_won_opportunities: el.closedWonOpportunities ?? 0,
              avg_deal_size_usd: roundCurrency(parseSpend(el.averageDealSizeInUsd)),
              avg_days_to_close: el.averageDaysToClose ?? 0,
            });
          }
        } catch (err) {
          console.log(JSON.stringify({
            event: "linkedin_ads_revenue_attribution_parse_warning",
            accountId,
            tenantId: ctx.tenantId,
            error: err instanceof Error ? err.message : String(err),
          }));
        }
      } else {
        // CRM revenue not available — graceful degradation (not all accounts have CRM)
        console.log(JSON.stringify({
          event: "linkedin_ads_revenue_attribution_unavailable",
          accountId,
          tenantId: ctx.tenantId,
          errorCode: revenueResult.error.code,
        }));
      }
    }

    return adapterOk({
      platform: "linkedin-ads",
      accountId,
      dateRange: { start: dateRange.start, end: dateRange.end },
      records,
      fetchedAt: new Date(),
      recordCount: records.length,
    });
  },

  // --------------------------------------------------------------------------
  // validateConnection (Subtask 1.5)
  // --------------------------------------------------------------------------
  async validateConnection(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<boolean>> {
    const accountId = ctx.accountId;
    if (!accountId) {
      return adapterErr({
        code: "account_not_found",
        message: "No LinkedIn Ad Account ID provided for connection validation.",
        retryable: false,
      });
    }

    // Scope validation
    if (!validateAdsScopes(credentials.scopes)) {
      return adapterErr({
        code: "permission_denied",
        message: "LinkedIn Ads requires r_ads and r_ads_reporting scopes.",
        retryable: false,
      });
    }

    const headers = linkedInAdsHeaders(credentials.accessToken);
    const url = `${LINKEDIN_REST_BASE}/adAccounts/${accountId}`;
    const result = await safeFetch(url, { headers }, 5_000);

    if (!result.success) {
      return handleLinkedInAdsFetchError(result);
    }

    return adapterOk(true);
  },

  // --------------------------------------------------------------------------
  // discoverAccounts (Subtask 1.6 — catalog contract per Story 31.21)
  // --------------------------------------------------------------------------
  async discoverAccounts(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<AccountInfo[]>> {
    // Scope validation — discoverAccounts also needs ads scopes
    if (!validateAdsScopes(credentials.scopes)) {
      return adapterErr({
        code: "permission_denied",
        message: "LinkedIn Ads requires r_ads and r_ads_reporting scopes. Please reconnect LinkedIn with Ads permissions.",
        retryable: false,
      });
    }

    // Ads family ONLY — zero organization endpoint requests (Story 31.21 AC #2)
    const discoveryResult = await discoverLinkedInAdAccounts({
      accessToken: credentials.accessToken,
      tenantId: ctx.tenantId,
      integrationId: ctx.integrationId,
    });

    if (!discoveryResult.ok) {
      // Strict semantics (Story 31.21 AC #4): discovery failure is adapterErr,
      // never a silent empty catalog. Route through parseLinkedInError for
      // per-cause codes (65604 → token_invalid, etc.) with safeFetch fallback.
      return handleLinkedInAdsFetchError({ success: false, error: discoveryResult.error });
    }

    // Defensive dedupe by id — first occurrence wins (ADR-0004 §Anti-Patterns 4)
    const dedupedAccounts = new Map<string, DiscoveredAdAccount>();
    for (const acc of discoveryResult.adAccounts) {
      if (!dedupedAccounts.has(acc.id)) dedupedAccounts.set(acc.id, acc);
    }

    const accounts: AccountInfo[] = [...dedupedAccounts.values()].map((acc) => ({
      id: acc.id,
      name: acc.name,
      platform: "linkedin-ads" as const,
      // Membership is NOT filtered by status — surface it for the picker UI
      metadata: {
        ...(acc.status !== undefined ? { status: acc.status } : {}),
        ...(acc.currency !== undefined ? { currency: acc.currency } : {}),
        ...(acc.reference !== undefined ? { reference: acc.reference } : {}),
        ...(acc.test !== undefined ? { test: acc.test } : {}),
      },
      // Display/consent metadata ONLY (31-4 contract) — fresh array per entry,
      // sourced from the single source of truth (no hardcoded drift)
      scopes: [...LINKEDIN_INTEGRATION_SCOPES["linkedin-ads"].scopes],
    }));

    return adapterOk(accounts);
  },

  // --------------------------------------------------------------------------
  // refreshToken (Subtask 1.9)
  // --------------------------------------------------------------------------
  async refreshToken(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<RefreshedTokens>> {
    if (!credentials.refreshToken) {
      return adapterErr({
        code: "token_expired",
        message: "No refresh token available. User must re-authorize LinkedIn.",
        retryable: false,
      });
    }

    const env = ctx.env as {
      LINKEDIN_CLIENT_ID?: string;
      LINKEDIN_CLIENT_SECRET?: string;
    };

    if (!env.LINKEDIN_CLIENT_ID || !env.LINKEDIN_CLIENT_SECRET) {
      return adapterErr({
        code: "invalid_response",
        message: "LinkedIn client credentials not configured",
        retryable: false,
      });
    }

    const result = await refreshLinkedInAccessToken(
      credentials.refreshToken,
      env.LINKEDIN_CLIENT_ID,
      env.LINKEDIN_CLIENT_SECRET
    );

    if (result.status === "error") {
      return adapterErr({
        code: "token_expired",
        message: result.error ?? "Failed to refresh LinkedIn token",
        retryable: false,
      });
    }

    if (!result.accessToken || result.expiresIn === undefined) {
      return adapterErr({
        code: "invalid_response",
        message: "LinkedIn token refresh returned incomplete data",
        retryable: false,
      });
    }

    const expiresAt = new Date(Date.now() + result.expiresIn * 1000);

    return adapterOk({
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
      expiresAt,
    });
  },

  // --------------------------------------------------------------------------
  // getAvailableMetrics (Subtask 1.7)
  // --------------------------------------------------------------------------
  getAvailableMetrics(): MetricDefinition[] {
    return LINKEDIN_ADS_METRICS;
  },

  // --------------------------------------------------------------------------
  // getAvailableDimensions (Subtask 1.7)
  // --------------------------------------------------------------------------
  getAvailableDimensions(): DimensionDefinition[] {
    return LINKEDIN_ADS_DIMENSIONS;
  },
};

// ============================================================================
// Exported helpers for testing
// ============================================================================

export {
  formatLinkedInDateRange as _formatLinkedInDateRange,
  clampAdsDateRange as _clampAdsDateRange,
  parseSpend as _parseSpend,
  parseCampaignUrn as _parseCampaignUrn,
  linkedInDateToStr as _linkedInDateToStr,
  linkedInAdsHeaders as _linkedInAdsHeaders,
  validateAdsScopes as _validateAdsScopes,
  roundCurrency as _roundCurrency,
  roundRate as _roundRate,
  buildMetricRecord as _buildMetricRecord,
  LINKEDIN_ADS_FIELDS as _LINKEDIN_ADS_FIELDS,
  LINKEDIN_ADS_BATCH_1 as _LINKEDIN_ADS_BATCH_1,
  LINKEDIN_ADS_BATCH_2 as _LINKEDIN_ADS_BATCH_2,
  LINKEDIN_ADS_BATCH_3 as _LINKEDIN_ADS_BATCH_3,
  MAX_RETENTION_DAYS as _MAX_RETENTION_DAYS,
  CAMPAIGN_BATCH_SIZE as _CAMPAIGN_BATCH_SIZE,
};
